import { Box } from "@mui/material";
import { Outlet, useLocation, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Accept } from "../pages/authn/Accept";
import { SendToApplication } from "../pages/authn/SendToApplication";
import { SignIn } from "../pages/authn/SignIn";
import { type Page as PageName, pageDef } from "../pages/pages";
import { ConsoleProvider, useConsole } from "../state/state";
import { NotAuthorized, PageHeader, Section } from "../ui/components/primitives";
import { FlexcellSpinner } from "../ui/theme/FlexcellMark";
import { useTokens } from "../ui/theme/theme";
import { Header } from "./Header";
import { Nav, useIsTenantPath, usePageLabel, usePageSection } from "./Nav";

// ---
// The shell every route renders inside
//
// It owns what is true regardless of where you are: whether there is a session, which tenants
// exist, and the chrome around the page. The route decides only which page.
//
// The gates run here rather than in each route because they are about the session, not the
// place. Signing in therefore leaves the URL alone — a deep link survives being asked to sign
// in, and lands where it was pointed afterwards.
// ---

const COLLAPSED_KEY = "flexauth.console.rail-collapsed";

/** Remembered per browser: a rail someone narrowed should still be narrow on the next visit. */
function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function Shell(): React.JSX.Element {
  // `strict: false` because most routes name no tenant: the shell has to work above them.
  const params = useParams({ strict: false }) as { tenant?: string };
  return (
    <ConsoleProvider tenant={params.tenant ?? ""}>
      <Chrome />
    </ConsoleProvider>
  );
}

function Chrome(): React.JSX.Element {
  const tokens = useTokens();
  const navigate = useNavigate();
  const {
    user,
    loading,
    tenants,
    tenantsLoaded,
    isStaff,
    tenant,
    controlScopes,
    platformToken,
    signOutEverywhere,
    refreshSession,
  } = useConsole();
  // The invitation link's shape, kept as a search parameter: those URLs are already in people's
  // mail and a tidier route would break every one of them.
  const search = useSearch({ strict: false }) as { invitation?: string };
  // Hooks, so they run on every render rather than only the ones that reach the chrome.
  // Here rather than in the rail: narrowing it is also what moves the mark in the bar, and two
  // components cannot share a fact only one of them holds.
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const [toggles, setToggles] = useState(0);
  const pathname = useLocation().pathname;
  const label = usePageLabel();
  const section = usePageSection();
  const insideTenant = useIsTenantPath();
  const invitation = search.invitation ?? null;

  if (invitation !== null && invitation !== "") {
    // Redeemed before any session exists, so it precedes the sign-in gate.
    return (
      <Accept
        token={invitation}
        onDone={() => {
          // Drop the token so a refresh does not retry a spent invitation.
          void navigate({ to: "/", search: {}, replace: true });
        }}
      />
    );
  }

  if (loading) return <Spinner />;

  if (user === null) {
    // No redirect: the URL is left alone so the place being asked for survives signing in.
    return <SignIn onDone={() => void refreshSession()} />;
  }

  // The reset page works on a link alone, so a signed-in visitor of any kind must still reach it.
  if (pathname !== "/reset-password") {
    // Not the console until it is known whom it is for: drawing the menus first is what showed a
    // customer's user the whole administration site.
    if (isStaff === null && !tenantsLoaded) return <Spinner />;
    if (isStaff === false) return <SendToApplication />;
  }

  const tenantName = tenants.find((each) => each.id === tenant)?.name ?? tenant;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        bgcolor: tokens.surface.bg,
      }}
    >
      <Header
        // The tenant leads every trail inside one, so a page always says which tenant it is
        // about without the reader having to look up at the switcher. A page outside a tenant —
        // Tenants, the token inspector — has none to name.
        trail={
          insideTenant ? [tenantName, ...(section === undefined ? [] : [section]), label] : [label]
        }
        toggles={toggles}
        collapsed={collapsed}
      />
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        <Nav
          tenant={tenant}
          tenants={[...tenants]}
          onSelectTenant={(next: string) => {
            // The same page in another tenant is a different place, so this is navigation
            // rather than a setting: the page is kept and only the tenant in the path changes.
            const page = insideTenant ? (pathname.split("/")[3] ?? "apis") : "apis";
            void navigate({ to: `/tenants/${next}/${page}` });
          }}
          scopes={[...controlScopes]}
          isPlatform={platformToken !== null}
          account={user.username ?? user.email}
          collapsed={collapsed}
          onAccount={() => void navigate({ to: "/account" })}
          onSignOut={signOutEverywhere}
          onToggleCollapsed={() => {
            const next = !collapsed;
            setCollapsed(next);
            setToggles((count) => count + 1);
            try {
              window.localStorage.setItem(COLLAPSED_KEY, next ? "1" : "0");
            } catch {
              // Not remembered; the rail simply opens wide again next time.
            }
          }}
        />
        {/* The page is a panel lying on the desk: one card, with the rail and the trail around
            it rather than beside it on surfaces of their own. */}
        <Box sx={{ flex: 1, minWidth: 0, display: "flex", pr: 2.5, pb: 2.5 }}>
          <Box
            component="main"
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              bgcolor: tokens.surface.card,
              border: `1px solid ${tokens.line.default}`,
              borderRadius: `${tokens.radius.xl}px`,
              boxShadow: tokens.shadow[3],
            }}
          >
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 6, pb: 10 }}>
              <Outlet />
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function Spinner(): React.JSX.Element {
  const tokens = useTokens();
  return (
    <Box
      sx={{ minHeight: "100vh", display: "grid", placeItems: "center", bgcolor: tokens.surface.bg }}
    >
      <FlexcellSpinner size={36} color={tokens.accent.base} title="Loading" />
    </Box>
  );
}

/** `/` — stands in for the first tenant the subject can reach, once that is known. */
export function TenantPage(): React.JSX.Element {
  const navigate = useNavigate();
  const { tenants, tenantsLoaded, loading, user } = useConsole();

  useEffect(() => {
    // Not until the list has actually arrived: "no tenants yet" and "no tenants" look the same
    // from here, and acting on the first sends everyone to the platform page by mistake.
    if (loading || user === null || !tenantsLoaded) return;
    const first = tenants[0]?.id;
    void navigate(
      first === undefined
        ? { to: "/platform", replace: true }
        : // The tenant, not a page inside it: which page is worth landing on depends on what
          // this caller holds there, and the index below that path is what works it out.
          { to: "/tenants/$tenant", params: { tenant: first }, replace: true },
    );
  }, [loading, user, tenants, tenantsLoaded, navigate]);

  return <Box />;
}

/**
 * A page that operates inside a tenant.
 *
 * Every one of them needs the same four things and the same answer when the token could not be
 * minted, which is a real state rather than an error: a subject whose roles are all project
 * scoped resolves into no control-plane token at all.
 */
export function withTenant(
  id: PageName,
  Page: (props: {
    tenant: string;
    token: string;
    projects: string[];
    scopes: string[];
  }) => React.JSX.Element,
): () => React.JSX.Element {
  return function TenantScoped(): React.JSX.Element {
    const { tenant, controlToken, projects, controlScopes, error } = useConsole();
    const label = usePageLabel();
    if (controlToken === null) {
      return (
        <>
          <PageHeader title={label} />
          <Section title="No control-plane token">
            <Box sx={{ typography: "body1" }}>
              Administration runs on a token for the selected tenant, and one could not be minted.
              That usually means this account holds no assignment in {tenant || "any tenant"}.
            </Box>
            {error !== null && <Box sx={{ typography: "body2", mt: 2 }}>{error}</Box>}
          </Section>
        </>
      );
    }
    // The scope comes from the page table, which the sidebar also reads — so a visible link
    // never leads to a refusal, and a page the caller may use is never hidden.
    const needs = pageDef(id).scope;
    if (needs !== undefined && !controlScopes.includes(needs)) {
      const { grantedBy } = pageDef(id);
      return (
        <>
          <PageHeader title={label} />
          <NotAuthorized scope={needs} {...(grantedBy === undefined ? {} : { grantedBy })} />
        </>
      );
    }
    return (
      <Page
        tenant={tenant}
        token={controlToken}
        projects={projects.map((project) => project.id)}
        scopes={[...controlScopes]}
      />
    );
  };
}
